import { addMinutes, formatRFC3339 } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';

export const getHourLists = () => {
  let currentDate = new Date(2014, 6, 15, 0, 0);
  const nextDate = '16';

  const listofHours = [];

  while (format(currentDate, 'd') !== nextDate) {
    listofHours.push(format(currentDate, 'p'));
    currentDate = addMinutes(currentDate, 15);
  }

  return listofHours;
};

export const getformatedStart = (date: Date, start: string, tz: string) => {
  if (start.length > 10) {
    return start;
  }
  const currDate = new Date(format(new Date(date), 'P') + ' ' + start);
  const buildingDate = utcToZonedTime(new Date(format(new Date(date), 'P') + ' ' + start), tz);
  const offset = (currDate.getTime() - buildingDate.getTime()) / 6e4;
  const result = addMinutes(currDate, offset);

  return formatRFC3339(result);
};

export const getformatedEnd = (date: Date, end: string, tz: string) => {
  if (end.length > 10) {
    return end;
  }
  const currDate = new Date(format(new Date(date), 'P') + ' ' + end);
  const buildingDate = utcToZonedTime(new Date(format(new Date(date), 'P') + ' ' + end), tz);
  const offset = (currDate.getTime() - buildingDate.getTime()) / 6e4;
  const result = addMinutes(currDate, offset);

  return formatRFC3339(result);
};

export const getDateFromTime = (time: string) => {
  const date = new Date();
  const timeSplit = time.split(':');
  date.setHours(parseInt(timeSplit[0]));
  date.setMinutes(parseInt(timeSplit[1]));
  date.setSeconds(parseInt(timeSplit[2]));
  return date;
};

/**
 * Converts a date object to YYYY-MM-DD string format
 */
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  // Pad the month and day with leading zeros if necessary
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Returns the start of the day for a given Date object in the specified timezone.
 * Note: we use the current day offset for the timezone, not the given date's offset.
 * We do this to satisfy our server endpoint (e.g. /apiv1/reports/bookings/).
 * @param date The input date.
 * @param tz The timezone string (e.g., "America/Edmonton").
 * @returns A string representing the start of the day in the given timezone.
 */
export function getBeginningOfDayString(date: Date, tz: string): string {
  // Format the current date to extract the timezone offset for the specified timezone
  const offsetString = format(new Date(), 'XXX', { timeZone: tz });

  const year = date.getFullYear();
  // Months are zero-indexed in JavaScript Date, so add 1 for human-readable format
  const month = date.getMonth() + 1;
  const day = date.getDate();

  // Pad single digits with leading zero
  const paddedMonth = month < 10 ? `0${month}` : month.toString();
  const paddedDay = day < 10 ? `0${day}` : day.toString();

  // Construct the final formatted date string
  const formattedDateString = `${year}-${paddedMonth}-${paddedDay}T00:00:00${offsetString}`;

  return formattedDateString;
}

/**
 * Returns the end of day in the given timezone, which is to say the beginning of the next day.
 * @param date
 * @param tz
 * @returns the end of day in the given timezone
 */
export function getEndOfDayString(date: Date, tz: string): string {
  // Get the next day
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);

  // Get the beginning of this next day
  return getBeginningOfDayString(nextDay, tz);
}

/**
 * Returns the start of the day for the given timezone
 * @param date
 * @param tz
 * @returns start of day date
 */
export function getBeginningOfDay(date: Date, tz: string): Date {
  return new Date(getBeginningOfDayString(date, tz));
}

/**
 * Returns the end of the day (i.e. start of the next day) for the given timezone.
 * @param date
 * @param tz
 * @returns end of day date
 */
export function getEndOfDay(date: Date, tz: string): Date {
  return new Date(getEndOfDayString(date, tz));
}

type FilterDateParams = {
  startOfMonth: Date;
  endOfMonth: Date;
  createdAtDate: Date;
};

/*
 * Use the `isDateWithinRange` to check if the createdAdDate falls within the
 * start and end dates (in the `mm/yyyy` format).
 * @returns true or false based on whether or not the input createdAtDate falls
 * within the startOfMonth and endOfMonth range.
 */
export function isDateWithinRange({ startOfMonth, endOfMonth, createdAtDate }: FilterDateParams) {
  // First, check if the years are the same for both the start and end dates
  if (startOfMonth.getFullYear() == endOfMonth.getFullYear()) {
    return (
      createdAtDate.getFullYear() == startOfMonth.getFullYear() &&
      createdAtDate.getMonth() >= startOfMonth.getMonth() &&
      createdAtDate.getMonth() <= endOfMonth.getMonth()
    );
  } else {
    // If the years are different, there are three cases to consider:
    // 1. Check if the createdAtDate is in the start year and after startOfMonth
    if (
      createdAtDate.getFullYear() == startOfMonth.getFullYear() &&
      createdAtDate.getMonth() >= startOfMonth.getMonth()
    ) {
      return true;
    }
    // 2. Check if the createdAtDate is in the end year and before endOfMonth
    if (
      createdAtDate.getFullYear() == endOfMonth.getFullYear() &&
      createdAtDate.getMonth() <= endOfMonth.getMonth()
    ) {
      return true;
    }
    // 3. Check if the createdAtDate is in a year between the start and end years
    if (
      createdAtDate.getFullYear() > startOfMonth.getFullYear() &&
      createdAtDate.getFullYear() < endOfMonth.getFullYear()
    ) {
      return true;
    }
  }
  return false;
}
